// src/consolidateSpecs.ts
import { appVisibleDocuments } from "@rock/manuals";
var TIER_ORDER = ["document", "research"];
function consolidateSpecs(input) {
  const candidates = collectAttributes(input).filter((attribute) => isStatedValue(attribute.value));
  const specs = input.specs;
  if (!specs || specs.length === 0) return mergeFreeForm(candidates);
  return [...fillDeclaredSpecs(specs, candidates), ...unslottedSpecs(specs, candidates)];
}
function unslottedSpecs(specs, candidates) {
  const declared = new Set(specs.map((spec) => foldForMatch(spec.code)));
  const claimed = (candidate) => specs.some((spec) => answers(candidate, foldForMatch(spec.code), foldForMatch(spec.label), declared));
  return mergeFreeForm(candidates.filter((candidate) => !claimed(candidate)));
}
function collectAttributes({ research, documents }) {
  const byTier = {
    document: appVisibleDocuments(documents).flatMap(
      (entry) => (entry.doc.digest?.specifications ?? []).map((spec) => ({ ...spec, source: "document" }))
    ),
    research: research.map((spec) => ({ ...spec, source: "research" }))
  };
  return TIER_ORDER.flatMap((tier) => byTier[tier]);
}
function fillDeclaredSpecs(specs, candidates) {
  const declared = new Set(specs.map((spec) => foldForMatch(spec.code)));
  const rendered = /* @__PURE__ */ new Set();
  const rows = [];
  for (const spec of specs) {
    const code = foldForMatch(spec.code);
    if (!code || rendered.has(code)) continue;
    const label = foldForMatch(spec.label);
    const winner = candidates.find((candidate) => answers(candidate, code, label, declared));
    if (!winner) continue;
    rendered.add(code);
    rows.push({ specId: spec.code, label: spec.label, value: winner.value, source: winner.source });
  }
  return rows;
}
function answers(candidate, code, label, declared) {
  const candidateCode = candidate.specId ? foldForMatch(candidate.specId) : "";
  if (candidateCode && declared.has(candidateCode)) return candidateCode === code;
  return !!label && foldForMatch(candidate.label) === label;
}
function mergeFreeForm(candidates) {
  const byLabel = /* @__PURE__ */ new Map();
  for (const attribute of candidates) {
    const key = foldForMatch(attribute.label);
    if (!key || byLabel.has(key)) continue;
    byLabel.set(key, attribute);
  }
  return [...byLabel.values()];
}
function isStatedValue(value) {
  const folded = value.trim().toLowerCase();
  if (!folded) return false;
  return !["n/a", "na", "unknown", "tbd", "unspecified", "-", "\u2014", "\u2013", "?"].includes(folded);
}
function foldForMatch(text) {
  return text.toLowerCase().replace(/[^a-z0-9]/g, "");
}

// src/enrichmentStale.ts
import { isMeaningfulBrandName, normalizeBrandName } from "@rock/util";

// src/specDrafts.ts
var MAX_CODE_LENGTH = 50;
function prepareSpecDrafts(existing, generated) {
  const current = existing ?? [];
  const taken = new Set(current.filter((spec) => spec.confirmed).map((spec) => foldSpecCode(spec.code)));
  const drafts = [];
  for (const draft of generated) {
    const code = foldSpecCode(draft.code);
    const label = draft.label.trim();
    if (!code || !label || taken.has(code)) continue;
    taken.add(code);
    drafts.push({ code, label, description: draft.description.trim(), confirmed: false });
  }
  const result = [];
  let next = 0;
  for (const spec of current) {
    if (spec.confirmed) result.push(spec);
    else if (next < drafts.length) result.push(drafts[next++]);
  }
  result.push(...drafts.slice(next));
  return orderSpecsByConfirmation(result);
}
function orderSpecsByConfirmation(specs) {
  return [...specs.filter((spec) => spec.confirmed), ...specs.filter((spec) => !spec.confirmed)];
}
function setSpecConfirmed(specs, code, confirmed) {
  const target = specs.find((spec) => spec.code === code);
  if (!target || target.confirmed === confirmed) return specs;
  const rest = specs.filter((spec) => spec.code !== code);
  return [
    ...rest.filter((spec) => spec.confirmed),
    { ...target, confirmed },
    ...rest.filter((spec) => !spec.confirmed)
  ];
}
function moveSpecWithinBlock(specs, code, delta) {
  if (!canMoveSpec(specs, code, delta)) return specs;
  const index = specs.findIndex((spec) => spec.code === code);
  const next = [...specs];
  const target = index + delta;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}
function canMoveSpec(specs, code, delta) {
  const index = specs.findIndex((spec) => spec.code === code);
  if (index < 0) return false;
  const neighbour = specs[index + delta];
  return !!neighbour && neighbour.confirmed === specs[index]?.confirmed;
}
function foldSpecCode(raw) {
  return raw.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, MAX_CODE_LENGTH).replace(/-+$/g, "");
}

// src/enrichmentStale.ts
function declaredSpecCodes(specs) {
  const codes = /* @__PURE__ */ new Set();
  for (const spec of specs ?? []) {
    const code = foldSpecCode(spec.code);
    if (code) codes.add(code);
  }
  return [...codes].sort();
}
function hasUsableDetails(details) {
  return isMeaningfulBrandName(details.brand) || details.attributes.length > 0;
}
function brandContradictsCache(model, brandInput) {
  if (!brandInput || model.brandId) {
    return false;
  }
  const cachedBrand = model.details?.brand;
  if (!isMeaningfulBrandName(cachedBrand)) {
    return false;
  }
  return normalizeBrandName(cachedBrand).key !== normalizeBrandName(brandInput).key;
}
function researchStaleReason(model, research, brandInput) {
  if (!model.details) return "the model has never been researched";
  if (!hasUsableDetails(model.details)) return "the cached research was a whiff (no brand, no attributes)";
  if (brandContradictsCache(model, brandInput)) {
    return `cached brand "${model.details.brand}" contradicts the observed brand "${brandInput}"`;
  }
  const identityChange = identityChangedReason(model);
  if (identityChange) return identityChange;
  return enrichmentStaleReason(model.enrichment, research.specs, research.promptVersion);
}
function identityChangedReason(model) {
  const recorded = model.enrichment?.brandId;
  if (recorded === void 0) return void 0;
  const current = model.brandId ?? null;
  if (recorded === current) return void 0;
  return `the catalog brand moved from ${recorded ?? "none"} to ${current ?? "none"} since the research ran`;
}
function enrichmentStaleReason(recorded, specs, promptVersion) {
  const declared = declaredSpecCodes(specs);
  if (!recorded) {
    return declared.length > 0 ? "the research predates the type having a spec list" : void 0;
  }
  if (recorded.promptVersion < promptVersion) {
    return `the research prompt moved from v${recorded.promptVersion} to v${promptVersion}`;
  }
  const asked = new Set(recorded.specCodes);
  const unasked = declared.filter((code) => !asked.has(code));
  if (unasked.length > 0) {
    return `the type's spec list now declares ${unasked.length} spec(s) the research was never asked for`;
  }
  return void 0;
}

// src/identityLevel.ts
import { hasAppVisibleDoc } from "@rock/manuals";
function deriveIdentityLevel(model, entries) {
  if (hasAppVisibleDoc(entries)) return "documented";
  if (model.brandId) return "identified";
  return "observed";
}

// src/planModelEnrichment.ts
import {
  ambientHuntBlockedReason,
  canClaimDocumentIngest,
  coverageEligible,
  deriveModelDocStatus,
  hasManual,
  INGEST_PENDING_STALE_MS,
  isSupersededEntry,
  mayRevisit,
  ownerManualDocument,
  planModelRevisit
} from "@rock/manuals";
import { recallCheckKey } from "@rock/recalls";
function planModelEnrichment(input) {
  const { model, entries, job } = input;
  const steps = [];
  const skipped = [];
  const decide = (name, outcome) => {
    if (typeof outcome === "string") skipped.push({ step: name, reason: outcome });
    else steps.push(outcome);
  };
  decide("identify", planIdentify(model));
  decide("research-specs", planResearch(model, input.research));
  const coverage = planCoverage(model, entries, input.coverageApplied);
  decide("apply-coverage", coverage);
  decide("arm-document-hunt", planArmHunt(model, entries, job, typeof coverage !== "string"));
  decide("revisit", planRevisit(model, entries, job, input.now, input.previous));
  for (const outcome of planDigests(entries, input.now)) decide("digest-document", outcome);
  decide("notify-manual", planNotify(entries, input.previous));
  decide("check-recalls", planCheckRecalls(model, input.recallsEpoch));
  decide("sync-unit-brands", planSyncUnitBrands(model, input.previous));
  decide("sync-doc-status", planDocStatus(model, entries, job));
  return { level: deriveIdentityLevel(model, entries), steps, skipped };
}
function formatEnrichmentPlan(plan) {
  const steps = plan.steps.length ? plan.steps.map((step) => step.step).join(", ") : "nothing";
  const skipped = plan.skipped.map((skip) => `${skip.step} (${skip.reason})`).join("; ");
  return `${plan.level} \u2192 ${steps}${skipped ? ` | skipped: ${skipped}` : ""}`;
}
function planIdentify(model) {
  const concludedAt = model.identity?.concludedAt;
  if (concludedAt) return `identity concluded at ${concludedAt}`;
  return { step: "identify", reason: "the identify work has not run to completion" };
}
function planResearch(model, research) {
  if (!research) return "not decidable here \u2014 needs the type spec list and the brand observation";
  const reason = researchStaleReason(model, research.contract, research.brandInput);
  return reason ? { step: "research-specs", reason } : "cached research stands";
}
function planCoverage(model, entries, applied) {
  if (applied) return "applied this pass";
  if (model.archivedAt) return "model is archived";
  if (!coverageEligible(model)) return "no catalog brand \u2014 coverage is keyed by brand";
  if (hasManual(entries)) return "already has a published owner manual";
  return { step: "apply-coverage", reason: "brand linked and no owner manual on file" };
}
function planArmHunt(model, entries, job, coveragePending) {
  if (coveragePending) return "coverage has not been applied yet this pass";
  const blocked = ambientHuntBlockedReason({ model, entries, job });
  if (blocked) return blocked;
  return { step: "arm-document-hunt", reason: "identity concluded, no job, no owner manual, coverage applied" };
}
function planRevisit(model, entries, job, now, previous) {
  if (!previous) return "no previous state \u2014 a creation carries its brand, nothing landed";
  if (!model.brandId) return "no catalog brand linked";
  if (previous.model.brandId === model.brandId) return "brand unchanged";
  if (!mayRevisit(model)) return "model is not settled \u2014 nothing to wake";
  const signal = "brand-linked";
  const revisitPlan = planModelRevisit(model, entries, job, signal, now);
  if (!revisitPlan) return "the revisit rules plan nothing (already flagged for this signal, or served)";
  return {
    step: "revisit",
    signal,
    plan: revisitPlan,
    reason: previous.model.brandId ? `brand moved from ${previous.model.brandId} to ${model.brandId} on a settled model` : "a catalog brand landed on a settled model"
  };
}
function planSyncUnitBrands(model, previous) {
  if (!previous) return "no previous state \u2014 a creation has no units on it yet";
  if (previous.model.brandId === model.brandId && previous.model.lineId === model.lineId) return "brand unchanged";
  const label = (side) => `${side.brandId ?? "none"}${side.lineId ? ` (line ${side.lineId})` : ""}`;
  return { step: "sync-unit-brands", reason: `brand moved from ${label(previous.model)} to ${label(model)}` };
}
function planNotify(entries, previous) {
  if (!previous) return "no previous state \u2014 a creation announces nothing";
  const manual = ownerManualDocument(entries);
  if (!manual) return "no live owner manual";
  if (manual.link.publishedSilently) return "the live manual was published silently";
  const before = ownerManualDocument(previous.entries);
  if (before?.doc.id === manual.doc.id) return "the live manual is unchanged";
  return {
    step: "notify-manual",
    docId: manual.doc.id,
    reason: before ? `manual ${before.doc.id} replaced by ${manual.doc.id}` : "an owner manual went live"
  };
}
function planDigests(entries, now) {
  const claimable = entries.filter(
    (entry) => !isSupersededEntry(entry) && canClaimDocumentIngest(entry.doc, INGEST_PENDING_STALE_MS, now)
  );
  if (claimable.length === 0) return ["no current linked document awaits a digest"];
  return claimable.map((entry) => ({
    step: "digest-document",
    docId: entry.doc.id,
    reason: entry.doc.ingest ? `ingest ${entry.doc.ingest.status} since ${entry.doc.ingest.at}` : "never digested"
  }));
}
function planCheckRecalls(model, recallsEpoch) {
  if (recallsEpoch === void 0) return "not decidable here \u2014 needs the recalls epoch";
  if (model.archivedAt) return "model is archived";
  const key = recallCheckKey(model);
  if (!model.brandId) {
    if (model.recallsCheckedKey === void 0 || model.recallsCheckedKey === key) {
      return "no catalog brand \u2014 recalls are gated by brand";
    }
    return {
      step: "check-recalls",
      epoch: recallsEpoch,
      key,
      reason: "brand or number changed since the last check"
    };
  }
  const checked = model.recallsCheckedEpoch;
  if (checked !== void 0 && checked >= recallsEpoch && model.recallsCheckedKey === key) {
    return `checked at recalls epoch ${checked}`;
  }
  return { step: "check-recalls", epoch: recallsEpoch, key, reason: checkRecallsReason(model, recallsEpoch) };
}
function checkRecallsReason(model, recallsEpoch) {
  const checked = model.recallsCheckedEpoch;
  if (checked === void 0) return "never checked against recalls";
  if (checked < recallsEpoch) return `checked at epoch ${checked}, now ${recallsEpoch}`;
  if (model.recallsCheckedKey === void 0) return "checked before the brand-and-number key was recorded";
  return "brand or number changed since the last check";
}
function planDocStatus(model, entries, job) {
  const derived = deriveModelDocStatus(model, entries, job);
  if (model.docStatus === derived) return `docStatus is current (${derived})`;
  return {
    step: "sync-doc-status",
    from: model.docStatus,
    to: derived,
    reason: `stored ${model.docStatus ?? "nothing"}, derived ${derived}`
  };
}
export {
  brandContradictsCache,
  canMoveSpec,
  consolidateSpecs,
  declaredSpecCodes,
  deriveIdentityLevel,
  enrichmentStaleReason,
  foldSpecCode,
  formatEnrichmentPlan,
  hasUsableDetails,
  identityChangedReason,
  moveSpecWithinBlock,
  orderSpecsByConfirmation,
  planModelEnrichment,
  prepareSpecDrafts,
  researchStaleReason,
  setSpecConfirmed
};
//# sourceMappingURL=index.js.map